import { useCallback } from 'react';
import { useAppStore } from '@/store/useAppStore';
import { exportSession as exportSessionJSON, importSession as importSessionJSON } from '@/store/persistence';
import { generateCSV } from '@gpa-scales/shared-logic/engine/exportCSV';
import { generateSRAR } from '@gpa-scales/shared-logic/engine/exportSRAR';
import { generatePDFReport } from '@gpa-scales/shared-logic/engine/exportPDF';
import { Button } from './ui/Button';

export function ExportBar() {
  const courses = useAppStore((s) => s.courses);
  const settings = useAppStore((s) => s.settings);
  const result = useAppStore((s) => s.result);
  const schoolContext = useAppStore((s) => s.schoolContext);

  const handleExportJSON = useCallback(() => {
    const json = exportSessionJSON(courses, settings);
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gpa-scales-session-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }, [courses, settings]);

  const handleImportJSON = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.json';
    input.onchange = (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => {
        const data = importSessionJSON(reader.result as string);
        if (data) {
          useAppStore.getState().importCourses(data.courses);
          useAppStore.getState().loadSettings(data.settings);
        }
      };
      reader.readAsText(file);
    };
    input.click();
  }, []);

  const handleExportCSV = useCallback(() => {
    if (courses.length === 0 || !result) return;
    const csv = generateCSV(courses, result);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gpa-scales-courses-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [courses, result]);

  const handleExportSRAR = useCallback(() => {
    if (courses.length === 0) return;
    const csv = generateSRAR(courses);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gpa-scales-srar-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [courses]);

  const handleExportPDF = useCallback(() => {
    if (courses.length === 0 || !result) return;
    const doc = generatePDFReport(courses, settings, result, schoolContext);
    doc.save(`gpa-scales-report-${new Date().toISOString().split('T')[0]}.pdf`);
  }, [courses, settings, result, schoolContext]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        onClick={handleExportCSV}
        title="Export courses and GPA summary as a spreadsheet (.csv)."
      >
        CSV
      </Button>
      <Button
        onClick={handleExportSRAR}
        title="Self-Reported Academic Record format (.csv) for colleges that require SRAR/SSAR entry."
      >
        SRAR
      </Button>
      <Button
        variant="primary"
        onClick={handleExportPDF}
        title="Printable PDF report of courses, GPA variants, and honors cap."
      >
        PDF Report
      </Button>
      <Button
        onClick={handleExportJSON}
        title="Save your courses and settings to a .json file you can reload later."
      >
        Save
      </Button>
      <Button
        onClick={handleImportJSON}
        title="Load courses and settings from a previously saved .json file."
      >
        Load
      </Button>
    </div>
  );
}
